import { useState, useEffect, useRef } from 'react';
import { useWakeLock } from '@/hooks/useWakeLock';
import { useVoiceGuidance } from '@/hooks/useVoiceGuidance';
import { X, Play, Pause, RotateCcw, Volume2, VolumeX, Home } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { StepperInput } from './StepperInput';
import { Label } from '@/components/ui/label';
import { playMetronomeTick } from '@/utils/audioUtils';
import BreathingBar from './BreathingBar';
import { EXERCISE_BACKGROUNDS, EXERCISE_BG_FALLBACK_COLOR } from '@/utils/exerciseBackgrounds';
import ExerciseCopyright from './ExerciseCopyright';
import ExerciseInstructionsDrawer from './ExerciseInstructionsDrawer';
import ExerciseTopLogo from './ExerciseTopLogo';

const Technique478Modal = ({ isOpen, onClose, pattern, onSessionComplete }) => {
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [phase, setPhase] = useState('ready'); // ready, inhale, hold, exhale
  useWakeLock(isActive && !isPaused);
  useVoiceGuidance(phase, isActive && !isPaused);
  const [progress, setProgress] = useState(0);
  const [cyclesCompleted, setCyclesCompleted] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [sessionDuration, setSessionDuration] = useState(5); // minutes
  const [remainingTime, setRemainingTime] = useState(300); // seconds
  const [elapsedTime, setElapsedTime] = useState(0);
  
  const intervalRef = useRef(null);
  const timerRef = useRef(null);
  const soundEnabledRef = useRef(true);

  // Keep ref in sync with state
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  const timings = {
    inhale: 4,
    hold: 7,
    exhale: 8
  };

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsActive(false);
      setIsPaused(false);
      setPhase('ready');
      setProgress(0);
      setCyclesCompleted(0);
      setRemainingTime(sessionDuration * 60);
    }
  }, [isOpen, sessionDuration]);

  // Update remaining time when session duration changes
  useEffect(() => {
    if (!isActive) {
      setRemainingTime(sessionDuration * 60);
    }
  }, [sessionDuration, isActive]);

  // Main breathing logic
  useEffect(() => {
    if (!isActive || isPaused) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    let phaseStartTime = Date.now();
    let currentPhase = phase === 'ready' ? 'inhale' : phase;
    if (phase === 'ready') setPhase('inhale');

    const getPhaseDuration = (p) => {
      if (p === 'inhale') return timings.inhale * 1000;
      if (p === 'hold') return timings.hold * 1000;
      if (p === 'exhale') return timings.exhale * 1000;
      return 1000;
    };

    intervalRef.current = null;
    let rafId = null;
    const tick = () => {
      const elapsed = Date.now() - phaseStartTime;
      const phaseDuration = getPhaseDuration(currentPhase);
      const prog = Math.min(elapsed / phaseDuration, 1);
      setProgress(prog);

      if (elapsed >= phaseDuration) {
        // Move to next phase
        if (currentPhase === 'inhale') {
          currentPhase = 'hold';
          setPhase('hold');
        } else if (currentPhase === 'hold') {
          currentPhase = 'exhale';
          setPhase('exhale');
        } else if (currentPhase === 'exhale') {
          currentPhase = 'inhale';
          setPhase('inhale');
          setCyclesCompleted(prev => prev + 1);
        }
        phaseStartTime = Date.now();
        setProgress(0);
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isActive, isPaused, phase]);

  // Metronome - tick every second
  useEffect(() => {
    if (!isActive || isPaused) return;
    
    if (soundEnabledRef.current) playMetronomeTick();
    
    const metronomeInterval = setInterval(() => {
      if (soundEnabledRef.current) playMetronomeTick();
    }, 1000);
    
    return () => clearInterval(metronomeInterval);
  }, [isActive, isPaused]);

  // Session timer
  useEffect(() => {
    if (!isActive || isPaused) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    timerRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
      setRemainingTime(prev => {
        if (prev <= 1) {
          // Session completed - save it
          if (onSessionComplete && elapsedTime > 10) {
            onSessionComplete({
              patternId: pattern?.id || '478-technique',
              patternName: pattern?.name || 'Technique 4-7-8',
              durationSeconds: elapsedTime,
              cyclesCompleted: cyclesCompleted
            });
          }
          setIsActive(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isActive, isPaused, elapsedTime, cyclesCompleted, onSessionComplete, pattern]);

  const handleStart = () => {
    setElapsedTime(0);
    setIsActive(true);
    setIsPaused(false);
    setPhase('inhale');
    setProgress(0);
  };

  const handlePause = () => setIsPaused(!isPaused);

  const handleReset = () => {
    // Save session if enough time elapsed
    if (onSessionComplete && elapsedTime > 10) {
      onSessionComplete({
        patternId: pattern?.id || '478-technique',
        patternName: pattern?.name || 'Technique 4-7-8',
        durationSeconds: elapsedTime,
        cyclesCompleted: cyclesCompleted
      });
    }
    setIsActive(false);
    setIsPaused(false);
    setPhase('ready');
    setProgress(0);
    setCyclesCompleted(0);
    setRemainingTime(sessionDuration * 60);
    setElapsedTime(0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  const getPhaseLabel = () => {
    if (phase === 'ready') return 'Prêt';
    if (phase === 'inhale') return 'Inspirez';
    if (phase === 'hold') return 'Retenez';
    if (phase === 'exhale') return 'Expirez';
    return '';
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <ExerciseCopyright />
      <div 
        className="relative w-full h-full flex flex-col overflow-hidden"
        style={{
          backgroundImage: `url(${EXERCISE_BACKGROUNDS.technique478})`,
          backgroundColor: EXERCISE_BG_FALLBACK_COLOR,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Dark overlay for better text visibility */}
        <div className="absolute inset-0 bg-indigo-950/40" />

        {/* Floating Navigation Buttons */}
        <div className="absolute top-4 left-4 z-20">
          <button
            onClick={onClose}
            className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full transition-colors"
            title="Retour aux exercices"
          >
            <Home size={20} className="text-white" />
          </button>
        </div>
        
        <div className="absolute top-4 right-4 z-20">
          <button
            onClick={onClose}
            className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full transition-colors"
            title="Fermer"
          >
            <X size={20} className="text-white" />
          </button>
        </div>

        <ExerciseTopLogo />

        {/* Main Content */}
        <div className="relative flex-1 overflow-y-auto px-4 pt-20 sm:pt-24 pb-40 flex flex-col lg:items-center lg:justify-center lg:pb-8">
          <div className="flex flex-col items-center my-auto gap-6 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:gap-6 lg:items-center w-full">
            
            {/* Settings Panel - Left - Show when not active OR when paused */}
            <div className="w-full max-w-xs order-3 lg:order-none lg:col-start-1 lg:row-start-1 lg:justify-self-end lg:w-64">
              {/* Timer as a stats card (moved from the top-center overlay so the
                  header stays free for the mobile brand logo). */}
              <div className="bg-white/85 backdrop-blur-md rounded-xl px-3 py-3 text-center border border-slate-300 shadow-md mb-4">
                <p className="text-slate-500 text-xs mb-1">Temps restant</p>
                <p className="text-slate-800 font-bold text-2xl" style={{ fontFamily: 'system-ui' }}>
                  {formatTime(remainingTime)}
                </p>
              </div>
              {(!isActive || isPaused) && (
                <div className="bg-white/90 backdrop-blur rounded-2xl p-5 shadow-xl">
                  <h3 className="font-semibold text-slate-700 mb-4">
                    {isPaused ? 'Pause - Réglages' : 'Réglages'}
                  </h3>
                  
                  <div className="space-y-5">
                    <div>
                      <Label className="text-sm text-slate-600 mb-2 block">Durée</Label>
                      <StepperInput
                        value={sessionDuration}
                        onChange={(v) => {
                          setSessionDuration(v);
                          if (isPaused) setRemainingTime(v * 60);
                        }}
                        min={1}
                        max={20}
                        step={1}
                        unit="min"
                        label="durée"
                        colorClass="cyan"
                        valueTestId="t478-duration-value"
                        incTestId="t478-duration-inc"
                        decTestId="t478-duration-dec"
                      />
                    </div>

                    <div className="pt-2 border-t border-slate-200">
                      <p className="text-sm font-medium text-violet-600 mb-2">Rythme 4-7-8</p>
                      <p className="text-xs text-slate-500">Inspiration {timings.inhale}s · Rétention {timings.hold}s · Expiration {timings.exhale}s</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Center - Wave (controls hidden on desktop, in right column) */}
            <div className="flex flex-col items-center order-1 lg:order-none lg:col-start-2 lg:row-start-1">

              {/* Breathing Bar (Gradient Vertical) */}
              <div className="w-full flex justify-center">
                <BreathingBar
                  phase={phase === 'hold' ? 'hold-in' : phase}
                  progress={progress}
                  phases={['inhale', 'hold-in', 'exhale']}
                  phaseDurations={{
                    inhale: timings.inhale,
                    'hold-in': timings.hold,
                    exhale: timings.exhale
                  }}
                  colors={{
                    inhale: '#818cf8', // indigo
                    'hold-in': '#c084fc', // purple
                    exhale: '#f472b6', // pink
                    ready: '#94a3b8'
                  }}
                />
              </div>

              {/* Phase indicator - Now integrated in BreathingBar, keep for compatibility */}
              {isActive && (
                <div className="mt-2 bg-white/80 backdrop-blur rounded-xl px-6 py-2 shadow-lg">
                  <span className="text-lg font-medium text-slate-600">{getPhaseLabel()}</span>
                </div>
              )}

              {/* Controls (mobile only — desktop in right column) */}
              <div className="flex items-center gap-4 mt-6 exercise-controls-mobile-fixed lg:hidden">
                <button
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  className="w-14 h-14 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                >
                  {soundEnabled ? (
                    <Volume2 className="w-6 h-6 text-slate-600" />
                  ) : (
                    <VolumeX className="w-6 h-6 text-slate-400" />
                  )}
                </button>

                <button
                  onClick={isActive ? handlePause : handleStart}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                >
                  {!isActive || isPaused ? (
                    <Play className="w-8 h-8 text-slate-600 ml-1" />
                  ) : (
                    <Pause className="w-8 h-8 text-slate-600" />
                  )}
                </button>

                <button
                  onClick={handleReset}
                  className="w-14 h-14 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                >
                  <RotateCcw className="w-6 h-6 text-slate-600" />
                </button>
              </div>

              {/* Cycle counter (mobile only) */}
              <div className="mt-4 bg-white/60 backdrop-blur rounded-xl px-4 py-2 lg:hidden">
                <span className="text-slate-600 font-medium">{cyclesCompleted} cycles</span>
              </div>
            </div>

            {/* RIGHT (desktop) — Vertical controls */}
            <div className="hidden lg:flex lg:col-start-3 lg:row-start-1 lg:justify-self-start lg:flex-col lg:items-center gap-4">
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="w-14 h-14 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                data-testid="t478-sound-btn-desktop"
              >
                {soundEnabled ? (<Volume2 className="w-6 h-6 text-slate-600" />) : (<VolumeX className="w-6 h-6 text-slate-400" />)}
              </button>
              <button
                onClick={isActive ? handlePause : handleStart}
                className="w-20 h-20 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                data-testid="t478-play-btn-desktop"
              >
                {!isActive || isPaused ? (<Play className="w-9 h-9 text-slate-600 ml-1" />) : (<Pause className="w-9 h-9 text-slate-600" />)}
              </button>
              <button
                onClick={handleReset}
                className="w-14 h-14 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                data-testid="t478-reset-btn-desktop"
              >
                <RotateCcw className="w-6 h-6 text-slate-600" />
              </button>
              <div className="bg-white/60 backdrop-blur rounded-xl px-4 py-2 mt-2">
                <span className="text-slate-600 font-medium">{cyclesCompleted} cycles</span>
              </div>
            </div>

            {/* Instructions - mobile only, desktop uses drawer */}
            <div className="w-full max-w-xs order-3 lg:hidden">
              {!isActive && (
                <div className="bg-white/90 backdrop-blur rounded-2xl p-5 shadow-xl">
                  <h3 className="font-semibold text-slate-700 mb-3">Instructions</h3>
                  <ul className="text-sm text-slate-600 space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 font-bold">1.</span>
                      Inspirez par le nez (4s)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 font-bold">2.</span>
                      Retenez votre souffle (7s)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-pink-500 font-bold">3.</span>
                      Expirez par la bouche (8s)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-slate-500 font-bold">4.</span>
                      Répétez le cycle
                    </li>
                  </ul>
                  <div className="mt-4 p-3 bg-violet-50 rounded-lg">
                    <p className="text-xs text-violet-700">
                      <strong>Idéal pour :</strong> Endormissement, anxiété, gestion du stress. Pratiquez 2 à 4 cycles.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Instructions drawer (desktop only) */}
        <ExerciseInstructionsDrawer testIdPrefix="t478">
          <div className="bg-white rounded-xl p-4 shadow border border-slate-200">
            <h3 className="font-semibold text-slate-700 mb-3">Instructions</h3>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2"><span className="text-indigo-500 font-bold">1.</span>Inspirez par le nez (4s)</li>
              <li className="flex items-start gap-2"><span className="text-purple-500 font-bold">2.</span>Retenez votre souffle (7s)</li>
              <li className="flex items-start gap-2"><span className="text-pink-500 font-bold">3.</span>Expirez par la bouche (8s)</li>
              <li className="flex items-start gap-2"><span className="text-slate-500 font-bold">4.</span>Répétez le cycle</li>
            </ul>
            <div className="mt-4 p-3 bg-violet-50 rounded-lg">
              <p className="text-xs text-violet-700">
                <strong>Idéal pour :</strong> Endormissement, anxiété, gestion du stress. Pratiquez 2 à 4 cycles.
              </p>
            </div>
          </div>
        </ExerciseInstructionsDrawer>
      </div>
    </div>
  );
};

export default Technique478Modal;
